import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Pause, Play, ScanSearch } from "lucide-react";
import { getFullScan, getScanCheckedIds, startFullScan, processFullScan, type FullScanRun } from "@/lib/api";
import { readScanCache, rememberScannedIds } from "@/lib/scan-cache";
import { formatTimestamp } from "@/lib/utils";

const playerHref = (id: string) => `/accounts?player=${encodeURIComponent(id)}`;

/** Account scans: the same resumable batch loop as the classic FullScanPanel. */
export function ScanCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const latest = useQuery({ queryKey: ["full-scan"], queryFn: getFullScan });
  const run = latest.data?.run;
  const [running, setRunning] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remembered, setRemembered] = useState(0);
  const stop = useRef(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  // Keep the id across a failed start response so Retry cannot start another run.
  const startRequest = useRef<{ id: string; excludeIds?: string[] } | null>(null);
  useEffect(() => {
    mounted.current = true;
    try { setRemembered(readScanCache(localStorage).length); }
    catch { setError("Browser scan history could not be read. Check that browser storage is enabled."); }
    return () => { mounted.current = false; stop.current = true; };
  }, []);

  function update(next: FullScanRun) {
    qc.setQueryData(["full-scan"], { run: next });
    void qc.invalidateQueries({ queryKey: ["flags"] });
    void qc.invalidateQueries({ queryKey: ["flag-player"] });
  }
  function remember(ids: string[]) {
    try { const count = rememberScannedIds(localStorage, ids); if (mounted.current) setRemembered(count); }
    catch { throw new Error("Could not save scan history in this browser. Check browser storage and available space"); }
  }
  async function recoverChecked(id: string) {
    let after = "";
    do {
      const page = await getScanCheckedIds(id, after);
      remember(page.ids);
      if (!page.next) break;
      after = page.next;
    } while (!stop.current);
  }
  async function scan(newOnly = false) {
    if (busy.current) return;
    busy.current = true; stop.current = false;
    setRunning(true); setPausing(false); setError(null);
    try {
      const current = await getFullScan();
      // Import the latest run, including batches whose response was lost.
      if (current.run) await recoverChecked(current.run.id);
      if (stop.current) return;
      let next: FullScanRun;
      if (current.run?.status === "running") next = current.run;
      else {
        startRequest.current ??= { id: crypto.randomUUID(), excludeIds: newOnly ? readScanCache(localStorage) : undefined };
        next = (await startFullScan(startRequest.current.id, startRequest.current.excludeIds)).run;
        startRequest.current = null;
      }
      update(next);
      while (!stop.current && next.status === "running") {
        const result = await processFullScan(next.id);
        next = result.run;
        update(next);
        remember(result.checkedIds ?? []);
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof Error && e.message === "scan_batch_busy"
        ? "Another batch is still running. Wait a moment, then resume; progress is saved."
        : `${e instanceof Error ? e.message : String(e)}. Progress is saved; resume to retry.`);
      void qc.invalidateQueries({ queryKey: ["full-scan"] });
    } finally {
      busy.current = false;
      if (mounted.current) { setRunning(false); setPausing(false); }
    }
  }

  const resumable = run?.status === "running" || running;
  return <section className="ws-card mo-scan" aria-label="Account scans">
    <div className="ws-card-head">
      <h2>Account scans</h2>
      <div className="ws-row mo-scan-actions">
        {resumable ? <button className="pw-button pw-button-primary" disabled={readOnly || running || latest.isPending} onClick={() => void scan()}><Play size={13} />{running ? "Scanning…" : "Resume"}</button> : <>
          <button className="pw-button pw-button-primary" disabled={readOnly || latest.isPending} title={`Skips ${remembered.toLocaleString()} accounts already checked in this browser`} onClick={() => void scan(true)}><ScanSearch size={13} />Scan new</button>
          <button className="pw-button" disabled={readOnly || latest.isPending} title="Rechecks every account" onClick={() => void scan()}>Full scan</button>
        </>}
        {running && <button className="pw-button" disabled={pausing} onClick={() => { stop.current = true; setPausing(true); }}><Pause size={13} />{pausing ? "Pausing…" : "Pause"}</button>}
      </div>
    </div>
    {(error || latest.error) && <p className="ws-alert" role="alert">{error ?? String(latest.error)}</p>}
    {running && <p className="ws-alert is-warn">Keep this page open while scanning.</p>}
    {run && <div className="mo-scan-run">
      <div className="mo-scan-status" role="status" aria-live="polite">
        <strong>{run.status === "completed" ? "Complete" : running ? "Scanning" : "Paused"} · {run.processed.toLocaleString()} / {run.total.toLocaleString()}</strong>
        <span className="ws-muted">{formatTimestamp(run.started_at)}</span>
      </div>
      <progress className="mo-progress" aria-label="Accounts scanned" value={run.processed} max={Math.max(1, run.total)} />
      <div className="mo-scan-counts">
        <span title="Passed the available checks; not proof of legitimate play">{run.clean.toLocaleString()} clean</span>
        <Link to="/flags?source=full_scan">{run.flagged.toLocaleString()} flagged</Link>
        <span>{run.skipped.toLocaleString()} skipped</span>
        <span className={run.errors ? "is-warn" : undefined}>{run.errors.toLocaleString()} errors</span>
        <span className="ws-muted mo-remembered" title="Clearing browser storage or switching browsers loses this history">{remembered.toLocaleString()} remembered here</span>
      </div>
      {run.issues.length > 0 && <details className="mo-issues"><summary><ChevronRight size={13} />{run.issues.length} skipped / incomplete</summary>
        <ul>{run.issues.map(issue => <li key={issue.player_id}><Link className="ws-mono" to={playerHref(issue.player_id)}>{issue.player_id}</Link><span>{issue.detail}</span></li>)}</ul>
      </details>}
    </div>}
  </section>;
}
